import {Component, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzAlgDataService,
  BokkzBtwDataService,
  BokkzClient,
  ClientEntity,
  DossierItem,
  DossierItemEntity,
  IntermediaryEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormBuilder, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {Subject, takeUntil} from 'rxjs';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {dispatchSavedItemEvent} from '@snelstart/domain-logic';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tasks-item',
  templateUrl: './feature-tasks-item.component.html',
  styleUrls: ['./feature-tasks-item.component.scss'],
  imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, SharedUiAlertModule, CommonModule]
})
export class FeatureTasksItemComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  private client = {} as ClientEntity;
  private intermediairy = {} as IntermediaryEntity;
  public dossierItemEntity = {} as DossierItemEntity;
  public dossierItemFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  constructor(
    private injector: Injector,
    private algDataService: BokkzAlgDataService,
    public dataService: BokkzBtwDataService,
    private toastService: ToastService,
    private fb: FormBuilder
  ) {
    this.dossierItemFg = this.fb.group<DossierItemEntity>({
      id: null as unknown as number,
      title: null as unknown as string,
      clientName: null as unknown as string,
      creator: null as unknown as string,
      dateCreation: null as unknown as Date,
      dateDeadline: null as unknown as Date,
      hasDeadline: null as unknown as boolean,
      isChecked: null as unknown as boolean,
      opmerkingen: null as unknown as string,
      attachment: null as unknown as string[] | null as unknown as boolean,
      nameAttachment: null as unknown as string,
      checked: null as unknown as boolean,
      type: null as unknown as string,
      year: null as unknown as string,
      handelingsperspectief: null as unknown as string
    });
  }

  ngOnInit(): void {
    this.dossierItemFg.get('opmerkingen')?.valueChanges.subscribe((melding) => {
      this.updateHandelingsperspectief(melding ?? '');
    });
    this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.intermediairy = value;
    });
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.client = value;
    });
    this.algDataService.workflowItem.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.dossierItemEntity = value;
      this.dossierItemFg.patchValue(value);
      this.updateHandelingsperspectief(value.opmerkingen ?? '');
    });
  }

  saveForm(): void {
    const bokkzClient = this.injector.get(BokkzClient);
    bokkzClient.postDossierItem(String(this.client.id), this.dossierItemFg.getRawValue()).subscribe((dossierItemEntity) => {
      if (dossierItemEntity) {
        const dossierItem: DossierItem = {
          id: dossierItemEntity.id !== undefined ? String(dossierItemEntity.id) : undefined,
          type: dossierItemEntity.type,
          jaar: dossierItemEntity.year,
          actie: dossierItemEntity.title,
          soort: dossierItemEntity.creator,
          open: dossierItemEntity.dateCreation,
          deadline: dossierItemEntity.dateDeadline,
          attachment: this.dossierItemFg.getRawValue().attachment !== undefined
        };
        if (dossierItem.id) {
          this.algDataService.updateWorkflowItem(this.dossierItemFg.getRawValue());
          dispatchSavedItemEvent({
            identifier: dossierItem.id!,
            result: dossierItem
          });
          this.dossierItemFg.patchValue(dossierItemEntity, {
            emitEvent: false
          });
          this.toastService.showSuccessMessage('Het dossier item is succesvol opgeslagen.');
        } else {
          this.toastService.showWarningMessage('Het dossier item is niet succesvol omgezet.');
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private readonly actieMeldingen: { match: string; actie: string }[] = [
    {
      match: 'Fiscaal nummer / burgerservicenummer / sofinummer aangever onjuist',
      actie: 'Ga naar de aangifte en controleer het BSN van de klant. Klopt het nummer niet? Wijzig het en verzend de aangifte opnieuw.\n\nLukt het niet of is het bericht nogmaals niet succesvol ontvangen? Stuur dan een e-mail naar klantenservice@snelstart.nl. Vermeld daarbij:\n• Je kantoorgegevens\n• Naam van de klant\n• Om welk bericht het gaat\n• De bovenstaande foutmelding.\n Wij nemen zo spoedig mogelijk contact met je op.',
    },
    {
      match: 'OB-nummer onjuist',
      actie: 'Ga naar de aangifte en controleer het omzetbelastingnummer van de klant. Klopt het nummer niet? Wijzig het en verzend de aangifte opnieuw.\n\nLukt het niet of is het bericht nogmaals niet succesvol ontvangen? Stuur dan een e-mail naar klantenservice@snelstart.nl. Vermeld daarbij:\n• Je kantoorgegevens\n• Naam van de klant\n• Om welk bericht het gaat\n• De bovenstaande foutmelding.\n Wij nemen zo spoedig mogelijk contact met je op.',
    },
    {
      match: 'Er is een',
      actie: 'Zie hierboven in de notitie. \n\nKom je er niet uit? Stuur dan een e-mail naar klantenservice@snelstart.nl. Vermeld daarbij:\n• Je kantoorgegevens\n• Naam van de klant\n• Om welk bericht het gaat\n• De bovenstaande foutmelding.',
    },

  ];

  private updateHandelingsperspectief(melding: string): void {
    const gevonden = this.actieMeldingen.find(({match}) =>
      melding.toLowerCase().includes(match.toLowerCase())
    );
    const actie = gevonden?.actie
      ?? 'Er is een nadere beoordeling van de foutmelding nodig. \n\nStuur daarom een e-mail naar klantenservice@snelstart.nl. Vermeld daarbij:\n• Je kantoorgegevens\n• Naam van de klant\n• Om welk bericht het gaat\n• De bovenstaande foutmelding.\n Wij nemen zo spoedig mogelijk contact met je op.\n\n Je hoeft op dit moment geen wijzigingen aan te brengen in het bericht (de aangifte, opgaaf).';

    this.dossierItemFg.get('handelingsperspectief')?.setValue(actie, {emitEvent: false});
  }

  kopieerEmailTekst(): void {
    const accountnaam = this.intermediairy.officeName ?? 'Onbekend kantoor';
    const accountcode = this.intermediairy.id ?? 'Onbekende kantoorcode';
    const klant = this.dossierItemFg.get('clientName')?.value ?? this.client.achternaam ?? 'Onbekende klant';
    const berichtType = this.dossierItemFg.get('title')?.value ?? 'Onbekend berichttype';
    const jaar = this.dossierItemFg.get('year')?.value ?? '';
    const foutmelding = this.dossierItemFg.get('opmerkingen')?.value ?? 'Geen foutmelding beschikbaar';
    const datumRaw = this.dossierItemFg.get('dateCreation')?.value as Date | null;
    const datum = datumRaw
      ? new Date(datumRaw).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'})
      : 'Onbekende datum';

    const emailTekst =
      `Geachte Snelstart klantenservice,\n\n` +
      `In mijn takenlijst binnen Snelstart Fiscaal staat een bericht en ik verzoek jullie dit bericht te bekijken.\n\n` +
      `Accountnaam: ${accountnaam}\n` +
      `Accountcode: ${accountcode}\n\n` +
      `Naam klant: ${klant}\n` +
      `Type bericht: ${berichtType}${jaar ? ' (' + jaar + ')' : ''}\n` +
      `Datum bericht: ${datum}\n\n` +
      `Foutmelding: "${foutmelding}"\n\n` +
      `Ik verneem graag spoedig van jullie.\n\n` +  // ← stray backtick verwijderd
      `Met vriendelijke groet,\n`;

    navigator.clipboard.writeText(emailTekst).then(() => {
      this.toastService.showSuccessMessage('De gegevens zijn gekopieerd naar het klembord. Je kunt ze nu plakken in je e-mail.');
    }).catch(() => {
      this.toastService.showErrorMessage('Kopiëren is niet gelukt. Selecteer de gegevens handmatig.');
    });
  }

}
